import {
  Box,
  lighten,
  Stack,
  TableCell,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { flexRender, Header, HeaderGroup } from '@tanstack/react-table';

import { getPinnedSx } from './pinnedSx';

interface TableHeaderRowProps<T> {
  headerGroup: HeaderGroup<T>;
  isRowSelectionEnabled: boolean;
  lastLeftColId: string | undefined;
  firstRightColId: string | undefined;
}

const ResizeHandle = <T,>({ header }: { header: Header<T, unknown> }) => (
  <Box
    data-testid='column-resize-handle'
    onMouseDown={header.getResizeHandler()}
    onTouchStart={header.getResizeHandler()}
    sx={{
      position: 'absolute',
      right: 0,
      top: '15%',
      bottom: '15%',
      width: 8,
      cursor: 'col-resize',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover > .resize-line': {
        bgcolor: 'primary.main',
        width: 3,
        opacity: 1,
      },
    }}
  >
    <Box
      className='resize-line'
      sx={{
        width: 2,
        height: '100%',
        borderRadius: 1,
        transition: 'width 0.1s, opacity 0.1s',
        bgcolor: header.column.getIsResizing() ? 'primary.main' : 'divider',
        opacity: header.column.getIsResizing() ? 1 : 0.6,
        ...(header.column.getIsResizing() ? { width: 3 } : {}),
      }}
    />
  </Box>
);

interface TableHeaderCellProps<T> {
  header: Header<T, unknown>;
  isRowSelectionEnabled: boolean;
  lastLeftColId: string | undefined;
  firstRightColId: string | undefined;
}

const TableHeaderCell = <T,>({
  header,
  isRowSelectionEnabled,
  lastLeftColId,
  firstRightColId,
}: TableHeaderCellProps<T>) => {
  const isSortable = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const FilterComponentFn = header.column.columnDef.meta?.FilterComponentFn;
  const canFilter = typeof FilterComponentFn === 'function';
  const canResize = header.column.getCanResize();

  return (
    <TableCell
      colSpan={header.colSpan}
      sx={{
        borderColor: 'divider',
        py: isRowSelectionEnabled ? 0.4 : 1.65,
        width: `var(--col-live-size-${header.column.id}, ${String(header.getSize())}px)`,
        minWidth: `var(--col-live-size-${header.column.id}, ${String(header.getSize())}px)`,
        maxWidth: `var(--col-live-size-${header.column.id}, ${String(header.getSize())}px)`,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...(canResize ? { position: 'relative' } : {}),
        ...getPinnedSx(
          header.column,
          true,
          header.column.id === lastLeftColId,
          header.column.id === firstRightColId
        ),
        ...(header.column.getIsPinned() ? { zIndex: 3 } : {}),
      }}
    >
      {isSortable ? (
        <Stack direction='row' alignItems='center'>
          <TableSortLabel
            active={!!sortDirection}
            direction={sortDirection === false ? undefined : sortDirection}
            onClick={header.column.getToggleSortingHandler()}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            <Box component='span' sx={visuallyHidden}>
              {sortDirection === 'desc'
                ? 'sorted descending'
                : 'sorted ascending'}
            </Box>
          </TableSortLabel>
          {canFilter ? flexRender(FilterComponentFn, header.column) : null}
        </Stack>
      ) : (
        <Stack direction='row' alignItems='center' gap={0.75}>
          {flexRender(header.column.columnDef.header, header.getContext())}
          {canFilter ? flexRender(FilterComponentFn, header.column) : null}
        </Stack>
      )}

      {canResize ? <ResizeHandle header={header} /> : null}
    </TableCell>
  );
};

const TableHeaderRow = <T,>({
  headerGroup,
  isRowSelectionEnabled,
  lastLeftColId,
  firstRightColId,
}: TableHeaderRowProps<T>) => {
  return (
    <TableRow
      sx={[
        (theme) => ({
          backgroundColor: theme.palette.grey[50],
        }),
        (theme) =>
          theme.applyStyles('dark', {
            backgroundColor: lighten(theme.palette.background.paper, 0.1),
          }),
      ]}
    >
      {headerGroup.headers.map((header) => (
        <TableHeaderCell
          key={header.id}
          header={header}
          isRowSelectionEnabled={isRowSelectionEnabled}
          lastLeftColId={lastLeftColId}
          firstRightColId={firstRightColId}
        />
      ))}
    </TableRow>
  );
};

export default TableHeaderRow;
